import * as React from "react";
import { X } from "lucide-react";
import { Button } from "./button";

/**
 * The dialogs open now, the last opened last. Escape closes only the top one:
 * were every open dialog to answer it, one Escape would close a form under a
 * dialog opened over it, and what was typed into the form with it.
 */
const openDialogs: symbol[] = [];

/**
 * A dialog, with the three things a hand-rolled one always forgets: Escape
 * closes it, the backdrop closes it, and focus goes INTO it on open and comes
 * back to where it was on close.
 *
 * `aria-modal` plus `role="dialog"` is what tells a screen reader the rest of
 * the page is not available. The heading is wired with `aria-labelledby` so the
 * dialog announces what it is rather than "dialog".
 */
export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  initialFocus,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** A field to type into at once, for a dialog that is only a field. */
  initialFocus?: React.RefObject<HTMLElement | null>;
}) {
  const panel = React.useRef<HTMLDivElement>(null);
  const returnTo = React.useRef<HTMLElement | null>(null);
  const titleId = React.useId();
  // The latest `onClose`, read when Escape is pressed. Not an effect
  // dependency: a caller passing a new function each render would re-run the
  // effect below, and every re-run takes focus out of the field being typed in.
  const close = React.useRef(onClose);
  React.useLayoutEffect(() => {
    close.current = onClose;
  });

  React.useEffect(() => {
    if (!open) return;
    returnTo.current = document.activeElement as HTMLElement | null;
    // The panel, not the first field: a dialog whose first control is a
    // destructive button would have it focused and one Enter away. A dialog
    // that is only a field names that field instead.
    (initialFocus?.current ?? panel.current)?.focus();
    const me = Symbol(titleId);
    openDialogs.push(me);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && openDialogs[openDialogs.length - 1] === me) close.current();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const at = openDialogs.indexOf(me);
      if (at !== -1) openDialogs.splice(at, 1);
      // Back to the control that opened it. Without this, closing a dialog
      // drops focus to <body> and the next Tab starts from the top of the page.
      returnTo.current?.focus?.();
    };
  }, [open, initialFocus, titleId]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-backdrop/50 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative z-10 w-full max-w-lg rounded-lg border border-border bg-card shadow-card outline-none"
      >
        <div className="flex items-center justify-between gap-3 border-b border-border-light px-4 py-3">
          <h2 id={titleId} className="text-sm font-semibold">
            {title}
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X aria-hidden className="h-4 w-4" />
          </Button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-border-light px-4 py-3">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * A confirmation that has to be READ, for the actions that cannot be undone.
 *
 * The confirming button is `destructive` and the cancel is the one focus lands
 * near, because the cheap version of this dialog -- an `onClick` straight on
 * Delete -- is how a published article disappears at 2am.
 */
export function ConfirmModal({
  open,
  title,
  body,
  confirmLabel,
  busy,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={busy}>
            {busy ? "Working…" : confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-sm text-secondary">{body}</div>
    </Modal>
  );
}
